import { Checkbox, Grid, GridColumn, GridRow, Input, Select, SelectOptionType } from '@bp/ui-components';
import { Form, Formik } from 'formik';
import { FormikHelpers } from 'formik/dist/types';
import { useTranslation } from 'react-i18next';
import { MultiValue } from 'react-select';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { useMemoizedClassNumberOptions } from '../../../hooks/useMemoizedClassNumberOptions';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import {
  FactorType,
  PersonRole,
  SortDirection,
  useCreateTeachingLoadFactorMutation,
  useTeachingLoadFactorsQuery,
  useUpdateTeachingLoadFactorMutation,
} from '../../../types/planung-graphql-client-defs';
import { interpreteAsNumber } from '../../../utils/helper';
import { showSuccessCreateToast, showSuccessUpdateToast, showUserErrorToast } from '../../../utils/toast';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { FactorsFormType, TeachingLoadFactorType } from '../types';
import { FactorInterpretationInfo } from './components/FactorInterpretationInfo';
import { submitFactor } from './utils/submitFactor';
import { schema } from './validation/schema';

type FactorTeacherFormProps = {
  selectedFactorUuid: string | null;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const FactorTeacherForm = ({ selectedFactorUuid, onLoad, onClose }: FactorTeacherFormProps) => {
  const { t } = useTranslation();
  const currentSchoolYear = useUserConfigContext().selectedSchoolYear;
  const context = useMemorizedCacheTag('TEACHINGLOADFACTOR');

  const [, createTeachingLoadFactor] = useCreateTeachingLoadFactorMutation();
  const [, updateTeachingLoadFactor] = useUpdateTeachingLoadFactorMutation();

  const { teacherData, gradeGroupData, subjectData, subjectContainerData } = useLoadBasicData({ pause: false });

  const [{ data: factorsData }] = useTeachingLoadFactorsQuery({
    variables: {
      where: { schoolYear: { uuid: currentSchoolYear?.uuid } },
      options: {
        sort: [{ active: SortDirection.Desc, factorType: SortDirection.Desc }],
      },
    },
    context,
  });

  const selectedFactor: TeachingLoadFactorType | null =
    factorsData?.teachingLoadFactors.find((f) => f.uuid === selectedFactorUuid) ?? null;

  const gradeGroups = gradeGroupData?.gradeGroups;
  const subjectContainers = subjectContainerData?.subjectContainers;
  const subjects = subjectData?.subjects;
  const teachers = teacherData?.people?.filter((person) =>
    person.organizationConnection.edges.some((edge) => edge.properties.name === PersonRole.Teacher),
  );

  // select options
  const classNumberOptions = useMemoizedClassNumberOptions();
  const gradeGroupsOptions = useCreateSelectOptions(gradeGroups, 'uuid', 'name');
  const subjectsOptions = useCreateSelectOptions(subjects, 'uuid', 'name');
  const teachersOptions = useCreateSelectOptions(teachers, 'uuid', 'displayName');
  const subjectContainersOptions: SelectOptionType[] = useCreateSelectOptions(subjectContainers, 'uuid', 'name');

  const handleSubmit = async (values: FactorsFormType, formikHelpers: FormikHelpers<FactorsFormType>) => {
    onLoad(true);

    const { error } = await submitFactor(
      values,
      selectedFactor,
      currentSchoolYear,
      context,
      updateTeachingLoadFactor,
      createTeachingLoadFactor,
    );

    if (error) {
      showUserErrorToast({ error });
    } else if (selectedFactor?.uuid) {
      showSuccessUpdateToast();
    } else {
      showSuccessCreateToast();
    }

    onLoad(false);
    formikHelpers.resetForm();
    onClose();
  };

  const initialValues: FactorsFormType = {
    uuid: selectedFactor?.uuid ?? null,
    factorType: FactorType.Person, // default "Lehrer"
    grades: selectedFactor?.grades ?? [],
    gradeGroups: selectedFactor?.gradeGroup?.map((item) => item.uuid),
    subjects: selectedFactor?.subject?.map((item) => item.uuid),
    value: selectedFactor?.value.toString() ?? '1',
    valueRaw: selectedFactor?.valueRaw ?? '1',
    valuePartial: selectedFactor?.valuePartial?.toString() ?? '1',
    valuePartialRaw: selectedFactor?.valuePartialRaw ?? '1',
    comment: selectedFactor?.comment ?? '',
    schoolYear: currentSchoolYear,
    teachingLoadScenario: selectedFactor?.scenario?.uuid ?? '',
    active: selectedFactor?.active ?? true,
    teachers: selectedFactor?.teachers.map((item) => item.uuid) ?? [],
    subjectContainers: selectedFactor?.subjectContainer.map((sc) => sc.uuid) ?? [],
  };

  return (
    <Formik<FactorsFormType>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {({
        values,
        handleChange,
        resetForm,
        dirty,
        isValidating,
        isSubmitting,
        setFieldTouched,
        setFieldValue,
        errors,
      }) => {
        const handleMultiSelectChange = async (name: keyof FactorsFormType, options: MultiValue<SelectOptionType>) => {
          await setFieldTouched(name, true);
          await setFieldValue(
            name,
            options.map((o) => o.value),
          );
        };

        return (
          <Form>
            <Grid useFormGap>
              <GridRow>
                <GridColumn width={11}>
                  <GridRow spacingBottom='s'>
                    <Select
                      placeholder={t('common.all')}
                      tooltipText={t('common.allIfNothingSelected')}
                      name='teachers'
                      onChange={async (options) =>
                        handleMultiSelectChange('teachers', options as MultiValue<SelectOptionType>)
                      }
                      defaultValue={values?.teachers?.map((teacherUuid) => {
                        return (
                          teachersOptions.find((option) => option.value === teacherUuid) ?? { value: null, label: null }
                        );
                      })}
                      options={teachersOptions}
                      isSearchable
                      isClearable
                      isMulti
                      label={t('routes.teacher.title')}
                    />
                  </GridRow>
                  <GridRow spacingBottom='s' spacingTop='s'>
                    <Select
                      tooltipText={t('common.allIfNothingSelected')}
                      placeholder={t('common.all')}
                      name='subjects'
                      onChange={async (options) =>
                        handleMultiSelectChange('subjects', options as MultiValue<SelectOptionType>)
                      }
                      options={subjectsOptions}
                      defaultValue={
                        selectedFactor?.subject
                          ? selectedFactor?.subject.map((item) => ({ label: item.name, value: item.uuid }))
                          : null
                      }
                      isSearchable
                      isClearable
                      isMulti
                      label={t('subject.title', { count: 2 })}
                    />
                  </GridRow>
                  <GridRow spacingBottom='s' spacingTop='s'>
                    <GridColumn width={6}>
                      <Select
                        tooltipText={t('common.allIfNothingSelected')}
                        placeholder={t('common.all')}
                        name='grades'
                        onChange={async (options) =>
                          handleMultiSelectChange('grades', options as MultiValue<SelectOptionType>)
                        }
                        defaultValue={classNumberOptions.filter((opt) => values.grades.includes(opt.value))}
                        options={classNumberOptions}
                        isSearchable
                        isClearable
                        isMulti
                        label={t('grades.title', { count: 2 })}
                      />
                    </GridColumn>
                    <GridColumn width={6}>
                      <Select
                        tooltipText={t('common.allIfNothingSelected')}
                        placeholder={t('common.all')}
                        name='gradeGroups'
                        onChange={async (options) =>
                          handleMultiSelectChange('gradeGroups', options as MultiValue<SelectOptionType>)
                        }
                        options={gradeGroupsOptions}
                        isSearchable
                        isClearable
                        isMulti
                        label={t('gradeGroups.title', { count: 1 })}
                        defaultValue={
                          selectedFactor?.gradeGroup
                            ? selectedFactor?.gradeGroup.map((item) => ({ label: item.name, value: item.uuid }))
                            : null
                        }
                      />
                    </GridColumn>
                  </GridRow>
                  <GridRow spacingTop='s'>
                    <Select
                      tooltipText={t('common.allIfNothingSelected')}
                      placeholder={t('common.all')}
                      name='subjectContainers'
                      onChange={async (options) =>
                        handleMultiSelectChange('subjectContainers', options as MultiValue<SelectOptionType>)
                      }
                      options={subjectContainersOptions}
                      value={subjectContainersOptions.filter((o) =>
                        values.subjectContainers?.includes(o.value as string),
                      )}
                      isSearchable
                      isMulti
                      isClearable
                      label={t('common.kind')}
                    />
                  </GridRow>
                </GridColumn>
                <GridColumn width={1} align='end'>
                  <Checkbox
                    className='mt-3'
                    checked={values.active}
                    name='active'
                    label={t('common.active.full')}
                    onChange={async (e) => {
                      await setFieldValue('active', e.target.checked);
                      await setFieldTouched('active', true);
                    }}
                  />
                </GridColumn>
              </GridRow>

              <GridRow headline={t('factors.title', { count: 1 })}>
                <GridColumn width={11}>
                  <GridRow spacingBottom='s'>
                    <GridColumn width={3}>
                      <Input
                        tooltipText={t('factors.tooltip')}
                        value={values.valueRaw}
                        name='value'
                        onChange={async (e) => {
                          await setFieldValue('valueRaw', e.target.value);
                          await setFieldValue('value', interpreteAsNumber(e.target.value));
                          await setFieldTouched('value');
                        }}
                        label={t('factors.title', { count: 1 })}
                        error={errors.value}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <FactorInterpretationInfo value={values.value} />
                    </GridColumn>
                    <GridColumn width={3}>
                      <Input
                        tooltipText={t('factors.tooltip')}
                        value={values.valuePartialRaw}
                        name='value'
                        onChange={async (e) => {
                          await setFieldValue('valuePartialRaw', e.target.value);
                          await setFieldValue('valuePartial', interpreteAsNumber(e.target.value));
                          await setFieldTouched('valuePartial');
                        }}
                        label={t('factors.titlePartial')}
                        error={errors.value}
                      />
                    </GridColumn>
                    <GridColumn width={3}>
                      <FactorInterpretationInfo value={values.valuePartial as string} />
                    </GridColumn>
                  </GridRow>
                  <GridRow spacingTop='s'>
                    <Input
                      name={'comment'}
                      onChange={handleChange}
                      label={t('common.comment')}
                      value={values.comment ?? ''}
                      error={errors.comment}
                    />
                  </GridRow>
                </GridColumn>
                <GridColumn width={1}>
                  <></>
                </GridColumn>
              </GridRow>
            </Grid>

            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
                disabled: isSubmitting || !dirty || isValidating,
              }}
              errors={errors}
              isLoading={isSubmitting}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
